import { FC, useCallback, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Input, Row } from 'antd';
import { TbSearch, TbUserPlus, TbUsersMinus } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { teamService } from '@/services';
import { VirtualizedList as List, SkeletonList } from '@/shared';
import { useResourceRoutePreview } from '@/shared/components/resource-route-preview/context';
import { Team, Teammate } from '@/types';
import { centerComponentInMiddleofViewWithoutAlignItems } from '@/utilities';

import InviteToTeamModal from './components/InviteToTeamModal';
import MemberListItem from './components/MemberListItem';

interface MembersProps {
    team: Team;
}

const Members: FC<MembersProps> = ({ team }): JSX.Element => {
    const teamId = team.id;
    const preview = useResourceRoutePreview();
    const [loading, setLoading] = useState(true);

    const [open, setOpen] = useState(false);

    const [searchInput, setSearchInput] = useState('');

    const [collaborators, setCollaborators] = useState<Teammate[]>([]);

    const handleGetTeamMembers = useCallback(async () => {
        try {
            setLoading(true);

            const teammates = await teamService.getCollaborators(teamId);

            setCollaborators(teammates);
            setLoading(false);
        } catch (error) {
            captureException(error);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 750);
        }
    }, [teamId]);

    useEffect(() => {
        handleGetTeamMembers();
    }, [handleGetTeamMembers]);

    const searchedCollaborators =
        searchInput !== ''
            ? collaborators.filter((teammate: any) => {
                  return (
                      // prettier-ignore
                      String(teammate?.user?.firstName).toLowerCase().includes(String(searchInput).toLowerCase()) ||
                      String(teammate?.user?.lastName).toLowerCase().includes(String(searchInput).toLowerCase()) ||
                      String(teammate?.user?.emailAddress).toLowerCase().includes(String(searchInput).toLowerCase())
                  );
              })
            : collaborators;

    const { token } = useAntDesignTheme();

    return (
        <>
            <Row style={{ width: '100%' }} wrap={false} gutter={[8, 0]}>
                <Col flex='auto'>
                    <Input
                        style={{ width: '100%' }}
                        prefix={<TbSearch style={{ color: token.colorTextQuaternary, marginRight: '0.5rem' }} />}
                        allowClear
                        placeholder='Search Team Members...'
                        value={searchInput}
                        onChange={(e: any) => {
                            setSearchInput(e.target.value);
                        }}
                    />
                </Col>
                {!preview && (
                    <Col flex='none'>
                        <Button
                            type='primary'
                            onClick={() => {
                                setOpen(true);
                            }}
                        >
                            <TbUserPlus size={'1rem'} style={{ verticalAlign: '-0.175rem' }} />
                            Add Members to Team
                        </Button>
                        <InviteToTeamModal
                            team={team}
                            collaborators={collaborators}
                            setCollaborators={setCollaborators}
                            open={open}
                            setOpen={setOpen}
                        />
                    </Col>
                )}
            </Row>
            {searchedCollaborators?.length !== 0 && !loading ? (
                <List
                    dataSource={searchedCollaborators}
                    renderItem={(teammate: any) => (
                        <MemberListItem
                            key={teammate?.id}
                            collaborator={teammate}
                            team={team}
                            collaborators={collaborators}
                            setCollaborators={setCollaborators}
                        />
                    )}
                />
            ) : loading ? (
                <SkeletonList listLength={16} minHeight='30rem' />
            ) : (
                <div
                    style={{
                        minHeight: '30rem',
                        ...centerComponentInMiddleofViewWithoutAlignItems,
                        textAlign: 'center',
                        color: token.colorTextTertiary,
                    }}
                >
                    <TbUsersMinus size='2.5rem' style={{ margin: '0 auto', marginBottom: '1rem' }} />
                    No Collaborators
                </div>
            )}
        </>
    );
};

export default Members;
